import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, FileText, Cookie, Scale, Loader2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FAVICON } from '../assets';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Página Legal Genérica
 * Renderiza: /privacy, /terms, /cookies, /legal-notice
 */
export const LegalPage = () => {
  const { type: paramType } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'es';
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Determinar el tipo basado en la URL
  const getTypeFromPath = () => {
    const path = location.pathname;
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    if (path === '/cookies') return 'cookies';
    if (path === '/legal-notice') return 'legal-notice';
    return paramType || 'privacy';
  };
  
  const type = getTypeFromPath();

  const pageConfig = {
    privacy: {
      icon: Shield,
      color: 'emerald',
      endpoint: '/api/legal/privacy'
    },
    terms: {
      icon: FileText,
      color: 'blue',
      endpoint: '/api/legal/terms'
    },
    cookies: {
      icon: Cookie,
      color: 'amber',
      endpoint: '/api/legal/cookies'
    },
    'legal-notice': {
      icon: Scale,
      color: 'purple',
      endpoint: '/api/legal/legal-notice'
    }
  };

  const config = pageConfig[type] || pageConfig.privacy;
  const Icon = config.icon;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}${config.endpoint}?lang=${lang}`);
        setContent(data);
      } catch (err) {
        console.error('Error fetching legal page:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, [type, lang, config.endpoint]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Volver</span>
          </Link>
          
          <div className="flex items-center gap-2">
            <img 
              src={FAVICON} 
              alt="LokAlGrow" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-white font-semibold hidden sm:inline">LokAlGrow</span>
          </div>
          
          <div className="w-20" /> {/* Spacer */}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Title */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`w-14 h-14 rounded-2xl bg-${config.color}-500/20 flex items-center justify-center`}>
            <Icon className={`w-7 h-7 text-${config.color}-400`} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {content?.title || 'Documento Legal'}
            </h1>
            {content?.version && (
              <p className="text-gray-400 text-sm mt-1">
                Versión {content.version} 
                {content.last_updated && ` • Actualizado: ${new Date(content.last_updated).toLocaleDateString(lang)}`}
              </p>
            )}
          </div>
        </div>

        {/* Markdown Content */}
        <article className="prose prose-invert prose-emerald max-w-none">
          <div className="bg-gray-800/50 rounded-2xl p-6 sm:p-8 border border-gray-700">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mb-6 pb-4 border-b border-gray-700" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-xl font-semibold text-white mt-8 mb-4" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-lg font-medium text-gray-200 mt-6 mb-3" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-300 mb-4 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-2" {...props} />,
                li: ({node, ...props}) => <li className="text-gray-300" {...props} />,
                a: ({node, ...props}) => <a className="text-emerald-400 hover:text-emerald-300 underline" {...props} />,
                strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                table: ({node, ...props}) => (
                  <div className="overflow-x-auto my-6">
                    <table className="w-full border-collapse border border-gray-700 text-sm" {...props} />
                  </div>
                ),
                thead: ({node, ...props}) => <thead className="bg-gray-700" {...props} />,
                th: ({node, ...props}) => <th className="border border-gray-600 px-4 py-2 text-left text-white font-medium" {...props} />,
                td: ({node, ...props}) => <td className="border border-gray-700 px-4 py-2 text-gray-300" {...props} />,
                hr: ({node, ...props}) => <hr className="border-gray-700 my-8" {...props} />,
                code: ({node, inline, ...props}) => 
                  inline 
                    ? <code className="bg-gray-700 px-1.5 py-0.5 rounded text-emerald-400 text-sm" {...props} />
                    : <code className="block bg-gray-700 p-4 rounded-lg overflow-x-auto text-sm" {...props} />
              }}
            >
              {content?.content || 'Contenido no disponible'}
            </ReactMarkdown>
          </div>
        </article>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <p className="text-gray-400 text-sm mb-4">Otros documentos legales:</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(pageConfig).map(([key, cfg]) => {
              if (key === type) return null;
              const PageIcon = cfg.icon;
              return (
                <Link
                  key={key}
                  to={`/${key}`}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  <PageIcon className="w-4 h-4" />
                  {key === 'privacy' && 'Privacidad'}
                  {key === 'terms' && 'Términos'}
                  {key === 'cookies' && 'Cookies'}
                  {key === 'legal-notice' && 'Aviso Legal'}
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} LokAlGrow. Todos los derechos reservados.</p>
          <p className="mt-2">
            <a href="mailto:support@lokalgrow.com" className="text-emerald-400 hover:underline">
              support@lokalgrow.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
